import { useBlocker } from 'react-router';
import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { ApiError } from './api';

export const cn = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------- formatting

const SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };
export const currencySymbol = (code = 'GBP') => SYMBOLS[code] ?? `${code} `;

/** An amount of money to the penny, always two places: 13.344 shows as £13.34. For totals, spend, value and the like. */
export function money(value: string | number | null | undefined, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n < 0 ? '−' : ''}${currencySymbol(currency)}${Math.abs(n).toFixed(2)}`;
}

/**
 * The price or cost of one piece. Parts cost pennies and fractions of a penny, so these keep the places they need
 * (up to four) and never fewer than two: 0.0059 stays 0.0059 rather than becoming 0.01, and 0.06 shows as 0.06.
 */
export function unitMoney(value: string | number | null | undefined, currency = 'GBP'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  let s = Math.abs(n).toFixed(4).replace(/0+$/, '');
  const dot = s.indexOf('.');
  if (dot === -1) s += '.00';
  else if (s.length - dot - 1 < 2) s = s.padEnd(dot + 3, '0');
  return `${n < 0 ? '−' : ''}${currencySymbol(currency)}${s}`;
}

/** A per-piece figure without the currency symbol, for input boxes: 0.10, 0.0059. */
export function unitPlain(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  let s = n.toFixed(4).replace(/0+$/, '');
  const dot = s.indexOf('.');
  if (dot === -1) s += '.00';
  else if (s.length - dot - 1 < 2) s = s.padEnd(dot + 3, '0');
  return s;
}

export const number = (n: number) => n.toLocaleString('en-GB');

/** 1 row, 2 rows. */
export const plural = (n: number, one: string, many = `${one}s`) => `${number(n)} ${n === 1 ? one : many}`;

export function when(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

// ---------------------------------------------------------------- controls

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const buttonStyles: Record<Variant, string> = {
  primary: 'bg-slate-900 text-white hover:bg-slate-700 disabled:bg-slate-300',
  secondary: 'bg-white text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50 disabled:text-slate-400',
  danger: 'bg-red-600 text-white hover:bg-red-500 disabled:bg-red-200',
  ghost: 'text-slate-700 hover:bg-slate-100 disabled:text-slate-400',
};

export function Button({
  variant = 'primary',
  small,
  large,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; small?: boolean; large?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed',
        small ? 'px-2.5 py-1 text-xs' : large ? 'min-h-12 px-5 py-3 text-base' : 'px-3.5 py-2 text-sm',
        buttonStyles[variant],
        className,
      )}
    />
  );
}

const fieldBase =
  'w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-sm placeholder:text-slate-400 disabled:bg-slate-100';

export const Input = ({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) => (
  <input {...rest} className={cn(fieldBase, className)} />
);
export const Select = ({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...rest} className={cn(fieldBase, className)} />
);
export const Textarea = ({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...rest} className={cn(fieldBase, className)} />
);

export function Field({ label, hint, children, className }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('block text-sm', className)}>
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export function Checkbox({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-4 rounded border-slate-300" />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------- layout atoms

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-lg border border-slate-200 bg-white shadow-sm', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

const badgeTones = {
  slate: 'bg-slate-100 text-slate-700',
  green: 'bg-emerald-100 text-emerald-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  blue: 'bg-sky-100 text-sky-800',
} as const;

export function Badge({ tone = 'slate', children }: { tone?: keyof typeof badgeTones; children: ReactNode }) {
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', badgeTones[tone])}>{children}</span>;
}

export const Spinner = ({ label = 'Loading…' }: { label?: string }) => (
  <div role="status" className="flex items-center gap-2 py-6 text-sm text-slate-500">
    <span className="size-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
    {label}
  </div>
);

export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const problems =
    error instanceof ApiError && Array.isArray((error.details as { problems?: unknown })?.problems)
      ? ((error.details as { problems: { row?: number; line?: number; message?: string; reason?: string }[] }).problems.slice(0, 8))
      : [];
  return (
    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      <p>{message}</p>
      {problems.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-xs">
          {problems.map((p, i) => (
            <li key={i}>
              {p.row ? `Row ${p.row}` : p.line ? `Line ${p.line}` : ''}: {p.message ?? p.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Notice = ({ tone = 'blue', children }: { tone?: 'blue' | 'green' | 'amber'; children: ReactNode }) => (
  <div
    role="status"
    className={cn(
      'rounded-md border px-3 py-2 text-sm',
      tone === 'green' && 'border-emerald-200 bg-emerald-50 text-emerald-900',
      tone === 'amber' && 'border-amber-200 bg-amber-50 text-amber-900',
      tone === 'blue' && 'border-sky-200 bg-sky-50 text-sky-900',
    )}
  >
    {children}
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">{children}</div>
);

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 pt-3 text-sm text-slate-600">
      <span>
        {from}–{to} of {number(total)}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" small disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {pages}
        </span>
        <Button variant="secondary" small disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium tracking-wide text-slate-500 uppercase">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {note && <div className="mt-1 text-xs text-slate-500">{note}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- asking before doing something

/**
 * An "are you sure" that lives in the page. The browser's own pop-up (window.confirm) can be blocked or swallowed
 * silently, for example in an embedded browser, which leaves a button that seems to do nothing. Use it as
 * `if (await confirm('Delete it?', { label: 'Delete', danger: true })) ...` and render `dialog` once.
 */
export function useConfirm() {
  const [asking, setAsking] = useState<{ message: string; label: string; danger: boolean; resolve: (ok: boolean) => void } | null>(null);
  const confirm = useCallback(
    (message: string, opts: { label?: string; danger?: boolean } = {}) =>
      new Promise<boolean>((resolve) => setAsking({ message, label: opts.label ?? 'Yes, go ahead', danger: !!opts.danger, resolve })),
    [],
  );
  const answer = (ok: boolean) => {
    asking?.resolve(ok);
    setAsking(null);
  };
  const dialog = asking ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Please confirm"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onKeyDown={(e) => e.key === 'Escape' && answer(false)}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <p className="text-sm text-slate-800">{asking.message}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => answer(false)}>
            Cancel
          </Button>
          <Button variant={asking.danger ? 'danger' : 'primary'} autoFocus onClick={() => answer(true)}>
            {asking.label}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
  return { confirm, dialog };
}

// ---------------------------------------------------------------- not losing work by leaving a page

/**
 * Stops you walking away from work you have not saved: the browser's back button or a trackpad swipe, a link, or
 * closing the tab. While `dirty`, leaving the page asks first. Render `dialog` once, and call `release()` just
 * before you navigate away on purpose (after saving).
 */
export function useLeaveGuard(dirty: boolean, message = 'You have work here that is not saved. If you leave, it is lost.') {
  const released = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !released.current && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      if (released.current) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const dialog =
    blocker.state === 'blocked' ? (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Leave without saving?"
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
        onKeyDown={(e) => e.key === 'Escape' && blocker.reset()}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
          <h2 className="font-semibold text-slate-900">Leave this page?</h2>
          <p className="mt-1 text-sm text-slate-700">{message}</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="danger" onClick={() => blocker.proceed()}>
              Leave and lose it
            </Button>
            <Button autoFocus onClick={() => blocker.reset()}>
              Stay here
            </Button>
          </div>
        </div>
      </div>
    ) : null;
  return { dialog, release: () => void (released.current = true) };
}
